import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Icon from '@material-ui/core/Icon';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import { InputBaseComponentProps } from '@material-ui/core/InputBase';
import InputLabel from '@material-ui/core/InputLabel';
import classNames from 'classnames';
import { CleaveOptions } from 'cleave.js/options';
import Cleave from 'cleave.js/react';
import React from 'react';
import { Maybe, Nullable } from '../Shared/Types';
import styles from './MaskedTextField.module.scss';
import { ReadOnlyField } from './ReadOnlyField';

export enum AutoCompleteTypes {
    TextBox = 'TextBox',
    UserInfo = 'UserInfo'
}

export enum AlignType {
    Left = 'left',
    Right = 'right'
}

export type MaskedTextFieldProps = {
    id?: string;
    label?: Nullable<string>;
    name?: string;
    value: string;
    required?: boolean;
    disabled?: boolean;
    placeholder?: string;
    errorText?: React.ReactNode;
    secondaryHelperText?: React.ReactNode;
    warningText?: React.ReactNode;
    helperText?: React.ReactNode;
    showHelperTextOnReadOnly?: boolean;
    autoFocus?: boolean;
    fullWidth?: boolean;
    maxLength?: number;
    hideCharacterLimit?: boolean;
    multiline?: boolean;
    readOnly?: boolean;
    rowsMax?: number;
    disableUnderline?: boolean;
    rows?: number;
    testId: string;
    hiddenLabel?: boolean;
    startAdornment?: React.ReactNode;
    disabledTruncation?: boolean;
    autoCompleteType?: AutoCompleteTypes;
    hideErrorText?: boolean;
    inputClasses?: {
        root?: string;
        input?: string;
    };
    singleSelect?: boolean;
    shrinkLabel?: boolean;
    hideWarningEmptyHelperText?: boolean;
    inputComponent?: React.ReactType<InputBaseComponentProps>;
    formatOptions?: CleaveOptions;
    clearEmbeddedIcon?: boolean;
    fillContainer?: boolean;
    partiallyFillContainer?: boolean;
    align?: AlignType;
    alphanumeric?: boolean;
    className?: string;
    inputRef?(element: Maybe<HTMLInputElement | HTMLTextAreaElement>): void;
    onBlur?(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void;
    onFocus?(e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void;
    onMouseDown?(e: React.MouseEvent<HTMLDivElement>): void;
    onKeyDown?(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void;
    filterFunction?(value: string): string;
    formatFunction?(value: string): string;
    unformatFunction?(value: string): string;
    onChange?(value: string, e?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
    onClear?(e: React.MouseEvent<HTMLButtonElement>): void;
};

type State = {
    cleaveInstance: Nullable<any>;
};

export class MaskedTextField extends React.Component<MaskedTextFieldProps, State> {
    get displayValue() {
        if (this.props.formatFunction) {
            return this.props.formatFunction(this.props.value || '');
        }

        return this.props.value || '';
    }
    public static muiName = 'FormControl';

    public state: State = {
        cleaveInstance: null
    };

    public inputElement: Maybe<HTMLInputElement>;

    private inputComponent = this.props.formatOptions
        ? (inputProps: any): any => {
              const { inputRef, ...other } = inputProps;

              return (
                  <Cleave
                      {...other}
                      onInit={this.initCleaveInstance}
                      htmlRef={inputRef}
                      options={this.props.formatOptions}
                      onChange={event => {
                          if (this.props.onChange) this.props.onChange(event.target.value, event);
                      }}
                  />
              );
          }
        : this.props.inputComponent
        ? this.props.inputComponent
        : undefined;

    public onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (this.props.readOnly) return;

        const cleanValue = this.props.unformatFunction ? this.props.unformatFunction(e.target.value) : e.target.value;
        const filteredValue = this.props.filterFunction ? this.props.filterFunction(cleanValue) : cleanValue;
        if (this.props.onChange) this.props.onChange(filteredValue, e);
    };

    public onClear = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (this.props.readOnly) return;

        setTimeout(() => {
            if (this.inputElement && this.inputElement.focus) this.inputElement.focus();
        }, 0);

        if (this.state.cleaveInstance) this.state.cleaveInstance.setRawValue('');

        if (this.props.onClear) this.props.onClear(e);
    };

    public bindElement = (element: Maybe<HTMLInputElement>) => {
        if (this.props.inputRef) this.props.inputRef(element);

        this.inputElement = element;
    };

    public render() {
        const {
            disabled,
            errorText,
            warningText,
            secondaryHelperText,
            helperText,
            label,
            id,
            readOnly,
            showHelperTextOnReadOnly,
            maxLength,
            hideCharacterLimit,
            testId,
            disabledTruncation,
            multiline,
            inputClasses,
            onClear,
            filterFunction,
            formatFunction,
            unformatFunction,
            singleSelect,
            shrinkLabel,
            autoCompleteType,
            formatOptions,
            hideWarningEmptyHelperText,
            clearEmbeddedIcon,
            fillContainer,
            partiallyFillContainer,
            alphanumeric,
            hiddenLabel,
            hideErrorText,
            ...otherProps
        } = this.props;

        let unifiedHelperText: React.ReactNode = errorText || warningText || helperText || secondaryHelperText || '';

        let helperTextType: 'normal' | 'warning' | 'unusual' | 'error' = 'normal';

        if (errorText) {
            helperTextType = 'error';
        } else if (warningText) {
            helperTextType = 'warning';
        }
        else if (secondaryHelperText)
        {
            helperTextType = 'unusual';
        }

        if (!unifiedHelperText) {
            if (!!maxLength && maxLength > 0 && !!hideCharacterLimit !== true) {
                unifiedHelperText = `${this.props.value.length}/${maxLength}`;
            }
        }

        if (readOnly) {
            return (
                <ReadOnlyField
                    multiLine={multiline}
                    label={!hiddenLabel ? label : null}
                    hideHelperText={hideWarningEmptyHelperText && unifiedHelperText === ''} //Legacy code
                    showHelperTextOnReadyOnly={showHelperTextOnReadOnly}
                    helperText={unifiedHelperText}
                    helperTextType={helperTextType}
                    alphanumeric={alphanumeric}
                >
                    {otherProps.startAdornment || this.displayValue}
                </ReadOnlyField>
            );
        }

        const inputClassesTotal = {
            root: classNames(
                styles.inputRoot,
                {
                    [styles.multiline]: multiline
                },
                inputClasses && inputClasses.root
            ),
            focused: styles.inputFocused,
            input: classNames(
                styles.input,
                {
                    [styles.truncated]: !disabledTruncation && !multiline && true,
                    [styles.singleSelect]: singleSelect
                },
                inputClasses && inputClasses.input
            ),
            error: helperTextType === 'warning' ? styles.warningUnderline : ''
        };

        const helperTextClasses = {
            root: classNames(styles.helperText, {
                [styles.warning]: helperTextType === 'warning'
            })
        };

        const autoCompleteValue: string =
            this.props.autoCompleteType === AutoCompleteTypes.UserInfo ? 'off-no-complete' : 'off';

        const clearAdornment: React.ReactNode = !!this.props.onClear &&
            !!this.displayValue &&
            !disabled &&
            !multiline &&
            !readOnly && (
                <InputAdornment
                    position='end'
                    classes={{
                        root: classNames(styles.clearAdornment, {
                            [styles.clearEmbeddedIcon]: !!clearEmbeddedIcon
                        })
                    }}
                >
                    <Button
                        onMouseDown={this.onClear}
                        size='small'
                        classes={{ root: styles.clearButton }}
                        tabIndex={-1}
                        data-test-id={`textField_clear_${this.props.testId}`}
                    >
                        <Icon classes={{ root: styles.iconRoot }}>cancel</Icon>
                    </Button>
                </InputAdornment>
            );

        const labelClasses = {
            root: styles.inputLabel
        };

        const inputLabel: React.ReactNode = !!label && (
            <InputLabel htmlFor={id} shrink classes={labelClasses}>
                {label || ''}
                {this.props.required && <span className={styles.requiredLabel}> *</span>}
            </InputLabel>
        );

        const formControlClasses = classNames(styles.formControl, {
            [styles.fillContainer]: fillContainer,
            [styles.partiallyFillContainer]: partiallyFillContainer,
            [styles.alignRight]: this.props.align === AlignType.Right,
            [styles.errorState]: helperTextType === 'error',
            [styles.unusual]: helperTextType === 'unusual'
        });

        return (
            <FormControl
                disabled={disabled}
                fullWidth
                error={helperTextType === 'error'}
                classes={{ root: formControlClasses }}
                data-test-id={`textFieldContainer_${this.props.testId}`}
                hiddenLabel={hiddenLabel}
            >
                {inputLabel}
                <Input
                    {...otherProps}
                    endAdornment={clearAdornment}
                    id={id}
                    value={this.displayValue}
                    autoComplete={autoCompleteValue}
                    disabled={disabled}
                    inputComponent={this.inputComponent}
                    inputProps={{
                        maxLength,
                        readOnly,
                        'data-lpignore': 'true',
                        'data-test-id': `textField_${this.props.testId}`
                    }}
                    inputRef={this.bindElement}
                    multiline={multiline}
                    rowsMax={this.props.rowsMax ? this.props.rowsMax : multiline ? '4' : undefined}
                    classes={inputClassesTotal}
                    error={!!errorText}
                    onKeyDown={this.props.onKeyDown}
                    onChange={this.onChange}
                />
                {!hideErrorText && (!hideWarningEmptyHelperText || unifiedHelperText) && (
                    <FormHelperText classes={helperTextClasses}>{unifiedHelperText || ' '}</FormHelperText>
                )}
            </FormControl>
        );
    }

    private initCleaveInstance = (cleave: any) => {
        this.setState({ cleaveInstance: cleave });
    };
}
